import { useEffect, useState } from 'react';
import { Box, Plus } from 'lucide-react';
import { operationsApi, type AssetType } from '../api';
import { PageHeader } from '@/lib/ui';

export function AssetTypeManagerPage() {
  const [items, setItems] = useState<AssetType[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      setItems(await operationsApi.listAssetTypes());
    } catch (e) {
      setError((e as Error).message || 'Failed to load asset types');
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <div className="p-6">
      <PageHeader
        title="Asset Types"
        icon={Box}
        subtitle="Define categories like Truck, Crane, Forklift. Each type can require a set of documents and an optional metadata schema for the registry form."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4" />
            New type
          </button>
        }
      />

      {error && (
        <div className="mt-3 rounded bg-rose-500/15 px-3 py-2 text-sm text-rose-300">{error}</div>
      )}

      <table className="mt-6 w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-2 pr-4">Slug</th>
            <th className="py-2 pr-4">Name</th>
            <th className="py-2 pr-4">Icon</th>
            <th className="py-2 pr-4">Required documents</th>
            <th className="py-2 pr-4">Enabled</th>
            <th className="py-2"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.id} className="border-b border-border/60">
              <td className="py-2 pr-4 font-mono text-xs">{t.slug}</td>
              <td className="py-2 pr-4">{t.name}</td>
              <td className="py-2 pr-4 text-muted-foreground">{t.icon}</td>
              <td className="py-2 pr-4 text-xs text-muted-foreground">
                {t.required_document_slugs.length > 0 ? t.required_document_slugs.join(', ') : '—'}
              </td>
              <td className="py-2 pr-4">{t.is_enabled ? '✓' : '✕'}</td>
              <td className="py-2 text-right text-xs">
                <button
                  className="mr-3 text-primary hover:underline"
                  onClick={async () => {
                    await operationsApi.patchAssetType(t.id, {
                      is_enabled: !t.is_enabled,
                    });
                    await refresh();
                  }}
                >
                  {t.is_enabled ? 'Disable' : 'Enable'}
                </button>
                <button
                  className="text-rose-300 hover:underline"
                  onClick={async () => {
                    if (!confirm(`Delete "${t.name}"?`)) return;
                    await operationsApi.deleteAssetType(t.id);
                    await refresh();
                  }}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                No asset types configured.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showCreate && (
        <AssetTypeCreateModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function AssetTypeCreateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('truck');
  const [requiredDocs, setRequiredDocs] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await operationsApi.createAssetType({
        slug: slug.trim(),
        name: name.trim(),
        icon,
        is_enabled: true,
        required_document_slugs: requiredDocs
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        metadata_schema: {},
      });
      onCreated();
    } catch (e) {
      setError((e as Error).message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <form onSubmit={onSubmit} className="card w-full max-w-md space-y-3 bg-surface p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">New asset type</h2>
          <button type="button" onClick={onClose}>
            ✕
          </button>
        </div>
        {error && (
          <div className="rounded bg-rose-500/15 px-3 py-2 text-sm text-rose-300">{error}</div>
        )}
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">Slug *</span>
          <input
            required
            pattern="[a-z0-9_-]+"
            className="input font-mono"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">Name *</span>
          <input
            required
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">Icon</span>
          <input className="input" value={icon} onChange={(e) => setIcon(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">
            Required document slugs (comma separated)
          </span>
          <input
            className="input"
            value={requiredDocs}
            onChange={(e) => setRequiredDocs(e.target.value)}
            placeholder="registration, insurance"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}
